import React from 'react';
import { Box, Typography, Card, Chip, Container } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface UserInfo {
  name: string;
  age: number;
  gender: string;
  contact: string;
  _id: string;
}

interface Booking {
  bookingId: string;
  userInfo: UserInfo[];
}

interface Batch {
  from: string;
  to: string;
  total: number;
  male: number;
  female: number;
  booking: Booking[];
}

interface TripData {
  _id: string;
  title: string;
  files: Array<{
    fileKey: string;
    fileType: string;
    featured: boolean;
    fileURL: string;
  }>;
  location: {
    lat: number;
    lng: number;
    name: string;
  };
  totalUpcomingBookings: number;
  batches: Batch[];
}

const BookingDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tripData = location.state?.tripData as TripData;

  // Use the first batches from the data
  const firstBatch = tripData?.batches?.[0];
  
  if (!firstBatch) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="h6">No booking data available</Typography>
      </Box>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const formatDateRange = (from: string, to: string) => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    
    const fromFormatted = fromDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
    
    const toFormatted = toDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
    
    return `${fromFormatted} - ${toFormatted}`;
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleBookingCardClick = (booking: Booking) => {
    navigate('/trip/bookinginfo', { 
      state: { 
        bookingData: {
        ...tripData,
        ...booking,
        batches: firstBatch,
        quantity: booking.userInfo?.length || 0,
        tripId: tripData._id,
          bookingId: booking.bookingId
        }
      } 
    });
  };



  if (!tripData) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="h6">No trip data available</Typography>
        <Typography variant="body2" color="text.secondary">
          Please go back and select a trip from the Bookings tab
        </Typography>
        <Box 
          onClick={() => window.history.back()}
          sx={{ 
            cursor: 'pointer',
            px: 3,
            py: 1,
            backgroundColor: '#008387',
            color: 'white',
            borderRadius: 2,
            '&:hover': { backgroundColor: '#006d70' }
          }}
        >
          Go Back
        </Box>
      </Box>
    );
  }



  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: '#f5f5f5',
      pb: 2
    }}>
      {/* Top Bar */}
      <Box sx={{ 
        height: 24, 
        backgroundColor: '#000', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        px: 2,
        color: 'white',
        fontSize: '12px'
      }}>
        <Typography variant="body2">9:30</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography variant="body2">📶</Typography>
          <Typography variant="body2">🔋</Typography>
        </Box>
      </Box>

      {/* Header */}
      <Box sx={{ 
        backgroundColor: 'white', 
        px: 2, 
        py: 1.5, 
        display: 'flex', 
        alignItems: 'center',
        gap: 2,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box 
          onClick={handleBack}
          sx={{ 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            '&:hover': { backgroundColor: '#f0f0f0' }
          }}
        >
          <ArrowBack sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Booking details
          </Typography>
          {tripData.title && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {tripData.title}
            </Typography>
          )}
          {tripData.location?.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              📍 {tripData.location.name}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Upcoming Bookings Summary */}
      <Box sx={{ 
        backgroundColor: '#9FA40029', 
        mx: 2, 
        mt: 2, 
        p: 2, 
        borderRadius: 2,
        textAlign: 'center'
      }}>
        <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 500 }}>
          Upcoming booking: {tripData?.totalUpcomingBookings || 0}
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ mt: 2 }}>
        {/* First Booking Section */}
        {firstBatch ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              mb: 2, 
              color: '#333',
              textAlign: 'center'
            }}>
              {formatDateRange(firstBatch.from, firstBatch.to)}
            </Typography>

            {/* Summary Counts */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 3, 
              justifyContent: 'center'
            }}>
              <Chip 
                label={`Total ${firstBatch.total || 0}`}
                sx={{ 
                  backgroundColor: '#f5f5dc',
                  color: '#333',
                  fontWeight: 500,
                  minWidth: 80
                }}
              />
              <Chip 
                label={`Male ${firstBatch.male || 0}`}
                sx={{ 
                  backgroundColor: '#f5f5dc',
                  color: '#333',
                  fontWeight: 500,
                  minWidth: 80
                }}
              />
              <Chip 
                label={`Female ${firstBatch.female || 0}`}
                sx={{ 
                  backgroundColor: '#f5f5dc',
                  color: '#333',
                  fontWeight: 500,
                  minWidth: 80
                }}
              />
            </Box>

                        {/* Individual Booking Entries */}
            {firstBatch.booking && firstBatch.booking.length > 0 ? (
              firstBatch.booking.map((booking, bookingIndex) => (
                <Box key={bookingIndex} sx={{ mb: 2 }}>
                  <Card 
                    key={bookingIndex}
                    onClick={() => handleBookingCardClick(booking)}
                    sx={{ 
                      p: 2, 
                      mb: 1, 
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      cursor: 'pointer'
                    }}
                  >
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Name: <span style={{ color: '#333', fontWeight: 500 }}>{booking.userInfo?.[0]?.name || 'N/A'}</span>
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Id: <span style={{ color: '#333', fontWeight: 500 }}>{booking.bookingId}</span>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Persons: <span style={{ color: '#333', fontWeight: 500 }}>{booking.userInfo?.length || 0}</span>
                      </Typography>
                    </Box>
                  </Card>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  No booking details available for this date range
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              No booking data available
            </Typography>
          </Box>
        )}


      </Container>
    </Box>
  );
};

export default BookingDetails;
